
"use client"

import { useState, useEffect } from "react"
import { useSearchParams } from "react-router-dom"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Textarea } from "@/components/ui/textarea"
import { supabase } from "@/integrations/supabase/client"
import { toast } from "sonner"
import { Separator } from "@/components/ui/separator"
import { cn } from "@/lib/utils"

type SelectedValues = {
  country: string
  experience: string
  site: string
  profession: string
  discipline: string
  role_type: string
  employment_type: string
}

interface FinalizeJDProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  selectedValues: SelectedValues
  jobDescription: string
  onConfirm: (editedDescription: string) => void
  onSuccess?: () => void
  selectedRoleId?: string | null
  selectedRoleIndex?: number
}

export function FinalizeJD({
  open,
  onOpenChange,
  selectedValues,
  jobDescription,
  onConfirm,
  onSuccess,
  selectedRoleId,
  selectedRoleIndex = 0
}: FinalizeJDProps) {
  const [searchParams] = useSearchParams()
  const projectId = searchParams.get('projectId')
  const [editedDescription, setEditedDescription] = useState("")
  const [showSuccessDialog, setShowSuccessDialog] = useState(false)
  const [showErrorDialog, setShowErrorDialog] = useState(false)
  const [errorMessage, setErrorMessage] = useState("")

  useEffect(() => {
    if (!jobDescription) return

    const formattedText = jobDescription
      .replace(/^-\s*/gm, '• ')
      .replace(/\n\s*\n\s*\n/g, '\n\n')
      .trim()

    setEditedDescription(formattedText)
  }, [open, jobDescription])

  const handleConfirm = async () => {
    if (!selectedRoleId) {
      setErrorMessage("No role selected to update")
      setShowErrorDialog(true)
      return
    }

    try {
      console.log('Starting handleConfirm with roleId:', selectedRoleId)

      // Update only the role_description and has_jd fields for the selected role
      // Save only the text content without PDF generation
      const { data: updateData, error: updateError } = await supabase
        .from('role_jd')
        .update({
          role_description: {
            text: editedDescription
          },
          has_jd: true
        })
        .eq('id', selectedRoleId)
        .select()

      console.log('Update response:', { updateData, updateError })

      if (updateError) {
        console.error('Update error:', updateError)
        setErrorMessage("Failed to update job description: " + updateError.message)
        setShowErrorDialog(true)
        return
      }

      onConfirm(editedDescription)
      if (onSuccess) {
        onSuccess()
      }
      
      // Reset scroll position to show the Role JD Table
      const viewport = document.querySelector('[data-radix-scroll-area-viewport]')
      if (viewport) {
        viewport.scrollTo({
          left: 40,
          behavior: 'smooth'
        })
      }
      
      setShowSuccessDialog(true)
      toast.success("Job description updated successfully")
    } catch (error) {
      console.error('Unexpected error in handleConfirm:', error)
      setErrorMessage("An unexpected error occurred: " + (error as Error).message)
      setShowErrorDialog(true)
    }
  }

  return (
    <>
      <AlertDialog open={open} onOpenChange={onOpenChange}>
        <AlertDialogContent className="max-w-[680px] h-[80vh] top-[50%] -translate-y-[50%] bg-gradient-to-b from-secondary/95 to-secondary border-primary/20 flex flex-col">
          <AlertDialogHeader className="space-y-2">
            <AlertDialogTitle className="text-lg font-bold bg-gradient-to-r from-primary to-primary-light bg-clip-text text-transparent">
              Finalize Job Description
            </AlertDialogTitle>
            <AlertDialogDescription className="text-white/80 text-[12px]">
              Review and edit the job description before finalizing
            </AlertDialogDescription>
            <div className="grid grid-cols-3 gap-2 p-2 bg-black/20 rounded-lg">
              <div className="space-y-1">
                <p className="text-[12px] text-primary-light">Role Type</p>
                <p className="text-[12px] text-white">{selectedValues.role_type}</p>
              </div>
              <div className="space-y-1">
                <p className="text-[12px] text-primary-light">Profession</p>
                <p className="text-[12px] text-white">{selectedValues.profession}</p>
              </div>
              <div className="space-y-1">
                <p className="text-[12px] text-primary-light">Discipline</p>
                <p className="text-[12px] text-white">{selectedValues.discipline}</p>
              </div>
            </div>
            <Separator className="bg-primary/20" />
          </AlertDialogHeader>
          <div className="flex-1 relative min-h-0">
            <Textarea
              value={editedDescription}
              onChange={(e) => setEditedDescription(e.target.value)}
              className={cn(
                "h-full resize-none text-[12px] leading-relaxed",
                "bg-black/40 text-white border-primary/20",
                "focus:ring-primary/30 focus:border-primary/30",
                "placeholder:text-white/50",
                "scrollbar-thin scrollbar-thumb-primary/20 scrollbar-track-transparent",
                "whitespace-pre-wrap"
              )}
              placeholder="Enter your job description here..."
            />
          </div>
          <AlertDialogFooter className="mt-2 space-x-2 bg-gradient-to-b from-secondary/95 to-secondary">
            <AlertDialogCancel className="bg-black/40 text-white hover:bg-black/60 border-primary/20 text-[12px]">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleConfirm}
              className="bg-primary hover:bg-primary-dark text-white border-none text-[12px]"
            >
              Confirm
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={showSuccessDialog} onOpenChange={setShowSuccessDialog}>
        <AlertDialogContent className="bg-secondary border-primary/20">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-primary">Success</AlertDialogTitle>
            <AlertDialogDescription className="text-white/80 text-[12px]">
              JD successfully generated and saved
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction 
              onClick={() => {
                setShowSuccessDialog(false)
                onOpenChange(false)
              }}
              className="bg-primary hover:bg-primary-dark text-white border-none text-[12px]"
            >
              OK
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={showErrorDialog} onOpenChange={setShowErrorDialog}>
        <AlertDialogContent className="bg-secondary border-primary/20">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-500">Error</AlertDialogTitle>
            <AlertDialogDescription className="text-white/80 text-[12px]">
              {errorMessage}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction 
              onClick={() => setShowErrorDialog(false)}
              className="bg-primary hover:bg-primary-dark text-white border-none text-[12px]"
            >
              OK
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
